import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Card } from '../../components/ui/Card';
import { SkeletonBlock, SkeletonLine, SkeletonText } from '../../components/ui/Loader';
import { ApiError } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { ReportButton } from '../moderation/ReportDialog';
import { comments as api, stamp, type Comment } from './api';
import { Textarea } from '../../components/ui/form';
import { ArtistHoverCard } from '../profile/ArtistHoverCard';
import { Verified } from '../../components/ui/Verified';

const MAX = 1000;

/**
 * The comments under a track, including waveform pins (HANDOFF §6.3).
 *
 * ## Pinning is opt-in, and it defaults to on while playing
 *
 * §6.3's whole distinctive feature is `position_ms`: a comment about a moment
 * rather than about the track. The box offers to anchor to wherever the playhead
 * is, checked by default when this track is what is playing and unchecked when
 * it is not — because a pin at 0:00 on a track you have not started is a pin
 * about nothing.
 */
export function CommentThread({ trackId }: { trackId: number }) {
  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();

  const current = usePlayer((state) => state.track);
  const position = usePlayer((state) => state.position);
  const isCurrent = current?.id === trackId;

  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(true);
  const [replyTo, setReplyTo] = useState<Comment | null>(null);

  const emoji = useEmojiComposer(body, setBody);

  const { data, isPending } = useQuery({
    queryKey: ['comments', trackId],
    queryFn: () => api.forTrack(trackId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['comments', trackId] });

  const post = useMutation({
    mutationFn: () =>
      api.create(trackId, {
        body: body.trim(),
        // A reply is about the conversation, not about a moment — pinning it
        // would put a mark on the waveform for something that is not about
        // that part of the track.
        position_ms: replyTo === null && pinned && isCurrent ? Math.round(position * 1000) : null,
        parent_id: replyTo?.id ?? null,
      }),
    onSuccess: () => {
      setBody('');
      setReplyTo(null);
      void refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.remove(id),
    onSuccess: refresh,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (body.trim() === '') return;

    post.mutate();
  };

  const items = data?.items ?? [];
  const roots = items.filter((item) => item.parent_id === null);
  const repliesOf = (id: number) =>
    // Replies read oldest-first: a thread is a conversation, and reading a
    // conversation backwards is work.
    items.filter((item) => item.parent_id === id).slice().reverse();

  return (
    <section className="mt-8" data-comments>
      <h2 className="text-lg font-semibold text-ink">
        {items.length === 0
          ? 'Comments'
          : `${items.filter((i) => !i.deleted).length} comment${items.filter((i) => !i.deleted).length === 1 ? '' : 's'}`}
      </h2>

      {/* ------------------------------------------------------------- */}
      {/* Writing                                                        */}
      {/* ------------------------------------------------------------- */}
      {user ? (
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3" data-comment-form>
          {replyTo && (
            <div className="flex items-center justify-between rounded-[var(--radius-control)] bg-raised px-3 py-2">
              <span className="text-xs text-soft">
                Replying to{' '}
                <span className="font-medium text-ink">
                  {replyTo.author?.display_name || replyTo.author?.username}
                  {replyTo.author?.verified && <Verified />}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="text-xs text-accent hover:underline"
              >
                Cancel
              </button>
            </div>
          )}

          <Textarea
            ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
            className="min-h-20"
            placeholder={replyTo ? 'Write a reply…' : 'Say something about this track…'}
            value={body}
            maxLength={MAX}
            disabled={post.isPending}
            data-comment-body
            onChange={(event) => setBody(event.target.value)}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" loading={post.isPending} disabled={body.trim() === ''}>
              {replyTo ? 'Reply' : 'Post'}
            </Button>

            {/* Downward: this composer sits above the thread rather than at the
                bottom of the viewport, so upward would open into the heading. */}
            <EmojiButton onSelect={emoji.insert} direction="down" align="left" />

            {/* Only for top-level comments, and only when this track is what is
                playing — otherwise the offer is to pin at a position that means
                nothing. */}
            {replyTo === null && isCurrent && (
              <label className="flex items-center gap-2 text-sm text-soft">
                <input
                  type="checkbox"
                  checked={pinned}
                  data-pin-toggle
                  onChange={(event) => setPinned(event.target.checked)}
                  className="h-4 w-4 rounded border-line bg-app accent-[var(--color-accent)]"
                />
                Pin at {stamp(Math.round(position * 1000))}
              </label>
            )}

            <span className="meta-caps ml-auto tabular-nums">
              {body.length}/{MAX}
            </span>
          </div>

          {post.isError && (
            <Alert tone="error">
              {post.error instanceof ApiError ? post.error.message : 'That could not be posted.'}
            </Alert>
          )}
        </form>
      ) : (
        <Card className="mt-4 p-4">
          <p className="text-sm text-soft">
            <Link to="/login" className="text-accent hover:underline">
              Sign in
            </Link>{' '}
            to leave a comment.
          </p>
        </Card>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Reading                                                        */}
      {/* ------------------------------------------------------------- */}
      {isPending ? (
        <div className="mt-6 flex flex-col gap-5" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3">
              <SkeletonBlock className="h-8 w-8 shrink-0 rounded-[var(--radius-pill)]" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <SkeletonLine width="30%" />
                <SkeletonText lines={2} />
              </div>
            </div>
          ))}
        </div>
      ) : roots.length === 0 ? (
        <p className="mt-6 text-sm text-soft">Nothing yet. Be the first.</p>
      ) : (
        <ul className="mt-6 flex flex-col gap-4" data-comment-list>
          {roots.map((comment) => (
            <li key={comment.id} data-comment={comment.id}>
              <Row
                comment={comment}
                onReply={user ? () => setReplyTo(comment) : undefined}
                onDelete={() => remove.mutate(comment.id)}
                deleting={remove.isPending && remove.variables === comment.id}
              />

              {repliesOf(comment.id).length > 0 && (
                <ul className="mt-3 flex flex-col gap-3 border-l border-line pl-4">
                  {repliesOf(comment.id).map((reply) => (
                    <li key={reply.id} data-comment={reply.id}>
                      <Row
                        comment={reply}
                        onReply={user ? () => setReplyTo(comment) : undefined}
                        onDelete={() => remove.mutate(reply.id)}
                        deleting={remove.isPending && remove.variables === reply.id}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Row({
  comment,
  onReply,
  onDelete,
  deleting,
}: {
  comment: Comment;
  onReply?: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const seek = usePlayer((state) => state.seek);

  /*
   * A tombstone, not a hidden row.
   *
   * The server sends no body and no author for a deleted comment, so there is
   * nothing here to hide. It stays in the list because a deleted parent still
   * holds its replies together.
   */
  if (comment.deleted) {
    return (
      <p className="text-sm italic text-faint" data-comment-deleted>
        This comment was deleted.
      </p>
    );
  }

  return (
    <div className="flex gap-3">
      <Avatar
        src={comment.author?.avatar ?? null}
        name={comment.author?.display_name || comment.author?.username || '?'}
        size={36}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {comment.author && (
            <ArtistHoverCard username={comment.author.username}>
              <Link
                to={`/${comment.author.username}`}
                className="text-sm font-medium text-ink hover:underline"
              >
                {comment.author.display_name || comment.author.username}
                {comment.author?.verified && <Verified />}
              </Link>
            </ArtistHoverCard>
          )}

          {comment.position_ms !== null && (
            // Clicking the timestamp seeks. It is the reason to pin a comment
            // at all - "listen to this bit" has to be one click.
            <button
              type="button"
              data-comment-stamp={comment.id}
              onClick={() => seek(comment.position_ms! / 1000)}
              className="rounded-[var(--radius-chip)] bg-raised px-1.5 py-0.5 text-xs tabular-nums text-accent transition-colors hover:bg-slate"
              aria-label={`Play from ${stamp(comment.position_ms)}`}
            >
              {stamp(comment.position_ms)}
            </button>
          )}

          <span className="meta-caps">{ago(comment.created_at)}</span>
        </div>

        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-soft">{comment.body}</p>

        <div className="mt-1.5 flex items-center gap-3">
          {onReply && (
            <button type="button" onClick={onReply} className="text-xs text-faint hover:text-ink">
              Reply
            </button>
          )}

          {comment.can_delete && (
            <button
              type="button"
              disabled={deleting}
              onClick={onDelete}
              data-comment-delete={comment.id}
              className="text-xs text-faint hover:text-hot disabled:opacity-50"
            >
              Delete
            </button>
          )}

          {/* Not on your own words: the delete button is already there, and
              reporting yourself is not a thing anybody means to do. */}
          {!comment.can_delete && (
            <ReportButton
              subjectType="comment"
              subjectId={comment.id}
              title="this comment"
            />
          )}
        </div>
      </div>

    </div>
  );
}

function ago(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;

  return `${Math.round(minutes / 1440)}d ago`;
}
